<script setup>
import FordButton from '@/components/FordButton.vue';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import {
  reactive,
  watch,
  ref,
} from 'vue';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';

import updateSolutionsOrder from './filterUtil';

const emit = defineEmits(['filter', 'clear']);

const props = defineProps({
  solutions: Array,
  orderings: Array,
  eventTypes: Array,
  inputs: {
    required: true,
    validator(val) {
      const hasProp = (prop) => (obj) => Object.prototype.hasOwnProperty.call(obj, prop);
      return [
        hasProp('eventTypes'),
        hasProp('solutions'),
        hasProp('ordering'),
      ].every((x) => x(val));
    },
  },
});

const solutionOptions = ref([]);
solutionOptions.value = updateSolutionsOrder(props.solutions);

watch(() => props.solutions, () => {
  solutionOptions.value = updateSolutionsOrder(props.solutions);
});

const stagedChanges = reactive({ ...props.inputs });
stagedChanges.solutions = updateSolutionsOrder([...props.inputs.solutions]);

watch(
  () => ({ ...props.inputs }),
  () => {
    stagedChanges.eventTypes = props.inputs.eventTypes;
    stagedChanges.solutions = updateSolutionsOrder(props.inputs.solutions);
    stagedChanges.ordering = props.inputs.ordering;
  },
);

const filter = () => emit('filter', stagedChanges);

const clear = () => {
  stagedChanges.eventTypes = [];
  stagedChanges.solutions = [];
  stagedChanges.ordering = props.orderings[1].value; // eslint-disable-line
};

const filterSectionOpen = ref(false);
const toggleFilterSection = () => {
  filterSectionOpen.value = !filterSectionOpen.value;
};
</script>
<template>
  <form @submit.prevent="filter" class="bg-ford-gray-light">
    <button
      @click="toggleFilterSection"
      class="block w-full font-FordAntennaMed text-sm py-3 tablet:hidden"
    >
      FILTER
      <font-awesome-icon
        :icon="faChevronDown"
        :class="{ '-rotate-180': filterSectionOpen }"
        class="text-base ml-3 transition-transform"
      />
    </button>
    <div
      :class="filterSectionOpen ? 'max-h-72' : 'max-h-0'"
      class="
        transition-maxHeight
        duration-300
        ease-in-out
        tablet:max-h-min
      "
    >
      <div
        class="
          max-w-[22.75rem]
          mx-auto
          px-4
          pt-6
          pb-9
          desktop:content-wrapper
          desktop:flex
          desktop:py-10
          desktop:gap-[3.75rem]
        "
      >
        <fieldset class="mb-4 desktop:mb-0 desktop:basis-[44%]">
          <!-- <legend>Event Types</legend> -->
          <div class="flex items-center gap-6 font-FordAntennaMed">
            <div class="hidden uppercase desktop:block">Filter:</div>
            <div
              v-for="{ name } in eventTypes"
              :key="name"
              class="flex items-center"
            >
              <input
                type="checkbox"
                :id="name"
                :value="name"
                v-model="stagedChanges.eventTypes"
                class="
                  h-[1.125rem]
                  desktop:h-[2rem]
                  w-[1.125rem]
                  desktop:w-[2rem]
                "
              />
              <label
                :for="name"
                class="
                  text-sm text-ford-blue-dark
                  capitalize
                  pl-[.6rem]
                  w-auto
                  desktop:text-base
                "
                >{{ name.toLowerCase() }}</label
              >
            </div>
          </div>
        </fieldset>
        <div class="mb-8 desktop:mb-0 desktop:basis-[42%]">
          <v-select
            v-model="stagedChanges.solutions"
            :options="solutionOptions"
            multiple
            class="bg-white rounded-none mb-[1rem] desktop:mb-[3rem]"
          ></v-select>

          <v-select
            v-model="stagedChanges.ordering"
            :options="orderings.map((x) => x.value)"
            class="bg-white rounded-none"
          ></v-select>
        </div>
        <div class="flex justify-between desktop:flex-col desktop:basis-[14%]">
          <ford-button
            class="desktop:mb-8"
            min-width="160"
            type="submit"
            @method="filter"
            >Search</ford-button
          >
          <ford-button min-width="160" @click.prevent="clear" transparent
            >Clear</ford-button
          >
        </div>
      </div>
    </div>
  </form>
</template>
